/** Estado da sessão.
 *
 * O access token não fica aqui: vive dentro do `ClienteHttp`, em memória.
 * Guardá-lo em estado do React o exporia a qualquer componente e tentaria
 * alguém a persistir "só para não perder o login ao recarregar" — que é
 * exatamente como uma sessão vaza por XSS.
 *
 * Recarregar a página não desloga: o cookie `HttpOnly` de refresh sobrevive, e
 * a montagem pede uma renovação silenciosa.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { http } from "@/lib/http";

import {
  concluirSegundoFator as concluirNaApi,
  entrar as entrarNaApi,
  pedeSegundoFator,
  cadastrar as cadastrarNaApi,
  renovar,
  sair as sairDaApi,
} from "./api";
import type { DadosDeCadastro, Tenant, Usuario } from "./api";

type Estado =
  /** Ainda tentando a renovação silenciosa. Não decide rota nenhuma. */
  | { situacao: "verificando" }
  | { situacao: "anonimo" }
  | {
      situacao: "autenticado";
      usuario: Usuario;
      tenant: Tenant;
      /** Esta sessão nasceu de um cadastro, e não de um login.
       *
       * É o que permite mostrar o guia de primeira vez **só a quem acabou de
       * criar a conta**. Sem isso, um usuário de meses que abrisse o produto
       * em outro navegador veria "registre a primeira oportunidade" tendo
       * cinquenta — e não há como saber que ele já viu o guia olhando só para
       * a sessão. Não sobrevive a um F5, de propósito: recarregar significa
       * que a pessoa já está dentro. */
      recemCadastrado?: boolean;
    };

interface Sessao {
  estado: Estado;
  /** `true` quando a senha valeu e falta o segundo fator (SPEC-IDN-002 § 3).
   *
   * O token de etapa **não** sai daqui: ele vive só dentro do provedor, em
   * memória, pelos cinco minutos que dura. Quem chama `concluirSegundoFator`
   * manda o código; quem guarda o token é este módulo. */
  segundoFatorPendente: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  concluirSegundoFator: (codigo: string) => Promise<void>;
  /** Desiste da etapa e volta ao formulário de e-mail e senha. */
  desistirDoSegundoFator: () => void;
  cadastrar: (dados: DadosDeCadastro) => Promise<void>;
  sair: () => Promise<void>;
  /** Relê a sessão sem passar pelo login — depois de ligar ou desligar o
   * segundo fator, para que `usuario.mfa_enabled` acompanhe. */
  reler: () => Promise<void>;
  temPermissao: (permissao: string) => boolean;
}

const ContextoDeSessao = createContext<Sessao | null>(null);

export function SessaoProvider({ children }: { children: ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ situacao: "verificando" });
  /** O token de etapa do MFA, enquanto ele existe. Em memória, nunca em disco. */
  const [tokenDeEtapa, setTokenDeEtapa] = useState<string | null>(null);
  const montado = useRef(true);

  const aplicar = useCallback(
    (
      resposta: { access_token: string; user: Usuario; tenant: Tenant },
      { recemCadastrado = false }: { recemCadastrado?: boolean } = {},
    ) => {
      http.definirToken(resposta.access_token);
      if (montado.current) {
        setEstado({
          situacao: "autenticado",
          usuario: resposta.user,
          tenant: resposta.tenant,
          recemCadastrado,
        });
      }
    },
    [],
  );

  const limpar = useCallback(() => {
    http.definirToken(null);
    if (montado.current) setEstado({ situacao: "anonimo" });
  }, []);

  // Renovação silenciosa na montagem. Sem ela, todo F5 cairia no login mesmo
  // com o cookie de refresh válido.
  useEffect(() => {
    montado.current = true;

    renovar()
      .then(aplicar)
      .catch(() => {
        limpar();
      });

    // Quando o cliente HTTP desiste de renovar, a sessão acabou de verdade.
    http.aoPerderSessao = limpar;

    return () => {
      montado.current = false;
      http.aoPerderSessao = null;
    };
  }, [aplicar, limpar]);

  const valor = useMemo<Sessao>(
    () => ({
      estado,

      segundoFatorPendente: tokenDeEtapa !== null,

      async entrar(email, senha) {
        const resposta = await entrarNaApi(email, senha);
        if (pedeSegundoFator(resposta)) {
          // Fica no estado do provedor, em memória. `localStorage` o exporia a
          // XSS pelo mesmo motivo que o access token nunca mora lá.
          setTokenDeEtapa(resposta.mfa_token);
          return;
        }
        aplicar(resposta);
      },

      async concluirSegundoFator(codigo) {
        if (tokenDeEtapa === null) {
          throw new Error("Não há segundo fator pendente.");
        }
        const sessao = await concluirNaApi(tokenDeEtapa, codigo);
        setTokenDeEtapa(null);
        aplicar(sessao);
      },

      desistirDoSegundoFator() {
        setTokenDeEtapa(null);
      },

      async reler() {
        aplicar(await renovar());
      },

      async cadastrar(dados) {
        aplicar(await cadastrarNaApi(dados), { recemCadastrado: true });
      },

      async sair() {
        // Não relança: quem pediu para sair não tem o que fazer com a falha, e
        // propagar viraria uma rejeição não tratada em cada botão de logout.
        //
        // A sessão local cai de qualquer forma. Deixar o usuário "logado" na
        // tela depois de ele pedir para sair é pior que perder a revogação no
        // servidor — que o refresh rotativo resolve sozinho ao expirar.
        try {
          await sairDaApi();
        } catch (falha) {
          console.warn("Falha ao encerrar a sessão no servidor.", falha);
        } finally {
          limpar();
        }
      },

      temPermissao(permissao) {
        if (estado.situacao !== "autenticado") return false;
        const { permissions } = estado.usuario;
        if (permissions.includes("*") || permissions.includes(permissao)) return true;
        const recurso = permissao.split(":")[0];
        return permissions.includes(`${recurso}:*`);
      },
    }),
    [estado, aplicar, limpar, tokenDeEtapa],
  );

  return <ContextoDeSessao.Provider value={valor}>{children}</ContextoDeSessao.Provider>;
}

/* eslint-disable-next-line react-refresh/only-export-components --
   o hook mora junto do provedor por coesão; separá-lo em outro arquivo só para
   agradar o fast refresh espalharia o contexto por dois lugares sem ganho. */
export function useSessao(): Sessao {
  const contexto = useContext(ContextoDeSessao);
  if (contexto === null) {
    throw new Error("useSessao precisa estar dentro de <SessaoProvider>.");
  }
  return contexto;
}
